/**
 * Copyright 2021-2022 Highway9 Networks Inc.
 */
import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "..";
import { networkService } from "../../services";
import { Network } from "../../types/network";
import { isPartnerKpiOnlyUser } from "~/helpers/partnerKpiOnly";

type initState = {
  open: boolean;
  data: Network | null;
};

const initialState: initState = {
  open: false,
  data: null,
};

export type FetchNetworkArg = {
  /** When true, run even for RAN_KPI_ONLY users (e.g. dashboard private network utilization PLMN). */
  skipPartnerKpiOnlyGuard?: boolean;
};

export const fetchNetwork = createAsyncThunk<Network[], FetchNetworkArg | void>(
  "network/fetch",
  async () => {
    try {
      const result = await networkService.getNetworks()
      return result;
    } catch (error) {
      console.error(error);
      throw error;
    }
  },
  // MCS-3777: backend returns 403 for RAN_KPI_ONLY; networks payload feeds TAC row (KPI-hidden in
  // SiteSummary) and usePlmnMap's H9-operator PLMN entry (partner users don't have it in userPlmn
  // anyway, so partnerPlmnList intersection is unchanged).
  {
    condition: (arg) =>
      Boolean((arg as FetchNetworkArg | undefined)?.skipPartnerKpiOnlyGuard) || !isPartnerKpiOnlyUser(),
  },
);

const networkSlice = createSlice({
  name: "network",
  initialState,
  reducers: {
    setOpen: (state, action: PayloadAction<boolean>) => {
      state.open = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchNetwork.fulfilled, (state, action) => {
        state.data = action.payload[0];
      })
      .addCase(fetchNetwork.rejected, (state, action) => {
        console.error(action.error);
      });
  },
});

export const networkActions = networkSlice.actions;
export default networkSlice.reducer;

export const networkOpen = (state: RootState) => state.network.open;
export const networkData = (state: RootState) => state.network.data;
